import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { EmployeeRecord } from '../types';
import toast from 'react-hot-toast';
import { FiEdit, FiEye, FiClock, FiCheck, FiX, FiAlertCircle, FiFileText } from 'react-icons/fi';
import { LoadingPill } from '../components/Skeleton';
import { useMinDelayed } from '../hooks/useMinDelayed';
import { api } from '../services/api';

const CandidateSubmissionPage: React.FC = () => {
  const { user } = useAuth();
  const [submission, setSubmission] = useState<EmployeeRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const showSkeleton = useMinDelayed(loading, 500);

  useEffect(() => {
    if (user?.role !== 'candidate') {
      return;
    }
    loadSubmission();
  }, [user]);

  const loadSubmission = async () => {
    try {
      setLoading(true);
      const response = await api.get('/candidate/submission');
      const data = response.data;
      if (data.success) {
        setSubmission(data.data);
      } else {
        toast.error(data.error || 'Failed to load submission');
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const errorData = error?.response?.data || {};

      if (status === 404) {
        if (errorData.error === 'orphaned_user') {
          console.log('Orphaned user detected, redirecting to registration...');
          toast.error('Your account needs to be re-registered. Redirecting...', {
            duration: 3000,
          });
          setTimeout(() => {
            const email = errorData.userEmail || user?.email || '';
            window.location.href = `/register?email=${encodeURIComponent(email)}`;
          }, 2000);
          return;
        }
        setSubmission(null); // No submission found (new user)
      } else {
        console.error('Error loading submission:', error);
        toast.error('Failed to load submission');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = (status: string) => {
    const statusConfig: Record<string, { icon: any; color: string; label: string; description: string }> = {
      new_submission: {
        icon: FiClock,
        color: 'text-blue-600 bg-blue-100',
        label: 'Application Submitted',
        description: 'Your application has been received and is awaiting review.'
      },
      under_review: {
        icon: FiEye,
        color: 'text-yellow-600 bg-yellow-100',
        label: 'Under Review',
        description: 'Our HR team is currently reviewing your application.'
      },
      requested_rework: {
        icon: FiEdit,
        color: 'text-orange-600 bg-orange-100',
        label: 'Updates Required',
        description: 'Please review and update your application based on the feedback provided.'
      },
      profile_verified: {
        icon: FiCheck,
        color: 'text-green-600 bg-green-100',
        label: 'Profile Verified',
        description: 'Your application has been verified and is being considered for hiring.'
      },
      available_for_hire: {
        icon: FiCheck,
        color: 'text-purple-600 bg-purple-100',
        label: 'Available for Hire',
        description: 'Congratulations! You are now in our hiring pool.'
      },
      hired: {
        icon: FiCheck,
        color: 'text-green-600 bg-green-100',
        label: 'Hired',
        description: 'Congratulations! You have been hired. HR will contact you with next steps.'
      },
      rejected: {
        icon: FiX,
        color: 'text-red-600 bg-red-100',
        label: 'Application Declined',
        description: 'Unfortunately, we cannot move forward with your application at this time.'
      },
      blacklisted: {
        icon: FiX,
        color: 'text-gray-600 bg-gray-100',
        label: 'Application Ineligible',
        description: 'Your application is not eligible for consideration.'
      }
    };

    return statusConfig[status] || {
      icon: FiAlertCircle,
      color: 'text-gray-600 bg-gray-100',
      label: status,
      description: 'Status information not available.'
    };
  };

  const handleUpdate = async (updatedData: any) => {
    try {
      const response = await api.put('/candidate/submission', updatedData);
      const data = response.data;
      if (data.success) {
        setSubmission(data.data);
        setIsEditing(false);
        toast.success('Application updated successfully');
      } else {
        toast.error(data.error || 'Failed to update application');
      }
    } catch (error) {
      console.error('Error updating submission:', error);
      toast.error('Failed to update application');
    }
  };

  if (user?.role !== 'candidate') {
    return (
      <div className="text-center py-8">
        <h1 className="text-2xl font-bold text-gray-900">Access Denied</h1>
        <p className="text-gray-600 mt-2">This page is only accessible to candidates.</p>
      </div>
    );
  }

  if (showSkeleton) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-bold text-gray-900">My Application</h1>
          <LoadingPill label="Loading your submission" />
        </div>
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <div className="skeleton-block h-6 w-1/3 rounded-md" aria-hidden="true" />
          <div className="skeleton-block h-4 w-2/3 rounded-md" aria-hidden="true" />
          <div className="skeleton-block h-4 w-1/2 rounded-md" aria-hidden="true" />
          <div className="skeleton-block h-32 w-full rounded-md" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="text-center py-8">
        <FiFileText className="mx-auto h-12 w-12 text-gray-400" />
        <h1 className="text-2xl font-bold text-gray-900 mt-4">No Application Found</h1>
        <p className="text-gray-600 mt-2">
          You haven't submitted an application yet.
        </p>
        <button
          onClick={() => window.location.href = '/register'}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Submit Application
        </button>
      </div>
    );
  }

  const statusDisplay = getStatusDisplay(submission.status);
  const StatusIcon = statusDisplay.icon;
  const canEdit = submission.status === 'requested_rework';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Application</h1>
            <p className="text-gray-600 mt-1">Reference ID: {submission.id}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Submitted</p>
            <p className="font-medium">{new Date(submission.createdAt).toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Status Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-4">
          <div className={`p-3 rounded-full ${statusDisplay.color}`}>
            <StatusIcon className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">{statusDisplay.label}</h2>
            <p className="text-gray-600">{statusDisplay.description}</p>
          </div>
          {canEdit && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
            >
              <FiEdit className="h-4 w-4" />
              <span>Update Application</span>
            </button>
          )}
        </div>
      </div>

      {/* Application Details */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Application Details</h3>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Personal Information */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">Personal Information</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Full Names:</span>
                <span className="ml-2 font-medium">{submission.fullNames}</span>
              </div>
              <div>
                <span className="text-gray-500">Surname:</span>
                <span className="ml-2 font-medium">{submission.surname}</span>
              </div>
              <div>
                <span className="text-gray-500">Email:</span>
                <span className="ml-2 font-medium">{submission.email}</span>
              </div>
              <div>
                <span className="text-gray-500">Contact Number:</span>
                <span className="ml-2 font-medium">{submission.contactNumber}</span>
              </div>
              <div>
                <span className="text-gray-500">Date of Birth:</span>
                <span className="ml-2 font-medium">{submission.dateOfBirth}</span>
              </div>
              <div>
                <span className="text-gray-500">Gender:</span>
                <span className="ml-2 font-medium">{submission.gender}</span>
              </div>
            </div>
          </div>

          {/* Employment Information */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">Employment Information</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Position Applied For:</span>
                <span className="ml-2 font-medium">{Array.isArray(submission.positionAppliedFor) ? submission.positionAppliedFor.join(', ') : submission.positionAppliedFor}</span>
              </div>
              <div>
                <span className="text-gray-500">Employment Status:</span>
                <span className="ml-2 font-medium">{submission.employmentStatus}</span>
              </div>
              {submission.currentEmployer && (
                <div>
                  <span className="text-gray-500">Current Employer:</span>
                  <span className="ml-2 font-medium">{submission.currentEmployer}</span>
                </div>
              )}
              {submission.currentPosition && (
                <div>
                  <span className="text-gray-500">Current Position:</span>
                  <span className="ml-2 font-medium">{submission.currentPosition}</span>
                </div>
              )}
            </div>
          </div>

          {/* Documents */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">Documents Submitted</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {submission.attachments?.map((attachment, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <FiCheck className="h-4 w-4 text-green-600" />
                  <span>{attachment.documentType}</span>
                </div>
              )) || <span className="text-gray-500">No documents uploaded</span>}
            </div>
          </div>

          {/* Review Notes (if available) */}
          {submission.reviewNotes && (
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Review Notes</h4>
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <p className="text-sm text-gray-700">{submission.reviewNotes}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal/Form would go here when isEditing is true */}
      {isEditing && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Update Application</h3>
              <button 
                onClick={() => setIsEditing(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>
            
            {/* Simple form - in a real app, you'd want to reuse your application form component */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Contact Number</label>
                <input
                  type="tel"
                  defaultValue={submission.contactNumber}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  onChange={(e) => {
                    // Handle form changes
                  }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Current Employer</label>
                <input
                  type="text"
                  defaultValue={submission.currentEmployer || ''}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Current Position</label>
                <input
                  type="text"
                  defaultValue={submission.currentPosition || ''}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // In a real implementation, collect form data and call handleUpdate
                    handleUpdate({
                      contactNumber: '1234567890', // Get from form
                      currentEmployer: 'Updated Company', // Get from form
                      currentPosition: 'Updated Position' // Get from form
                    });
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateSubmissionPage;
